/// <amd-module name="Artifacts/PackageRecycleBin/containers/PackageRecycleBinHubContainer" />

import { EmptyRecycleBinModalId } from "Artifacts/PackageRecycleBin/Components/EmptyRecycleBinModal";
import { PackageRecycleBinHub } from "Artifacts/PackageRecycleBin/components/PackageRecycleBinHub";
import { useCrossProtocolCommands } from "Artifacts/PackageRecycleBin/hooks/RecycleBinCommands";
import {
  IPackageRecycleBinModuleState,
  PackageRecycleBinActions as HubActions,
  PackageRecycleBinReduxModule,
} from "Artifacts/PackageRecycleBin/redux/Index";
import { IDeletedPackageVersion } from "Artifacts/PackageRecycleBin/types";
import { ComponentState } from "Artifacts/Root/Constants";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { getProtocolReduxModules } from "Artifacts/Root/protocols/Redux";
import * as Tags from "Artifacts/Root/protocols/TagConstants";
import { connectDynamicModules } from "Artifacts/Root/redux";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { packageRecycleBinHubContainerName } from "Artifacts/Root/routing/Definitions";
import * as React from "react";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import { IListSelection, ListSelection } from "VSSUI/List";

interface IPackageRecycleBinHubContainerMapStateToProps {
  componentState: ComponentState;
  pkgVersions?: IDeletedPackageVersion[];
}

const Actions = {
  initLoad: HubActions.initLoad,
  emptyRecycleBin: HubActions.emptyRecycleBin,
  hideModal: RootActions.hideModal,
  showModal: RootActions.showModal,
  fakeEmpty: HubActions.fakeEmptyOnClient,
};

type IPackageRecycleBinContainerProps = IPackageRecycleBinHubContainerMapStateToProps &
  typeof Actions;

const commandTagFilters = new Set([Tags.RecycleBin]);

const EmptyRecycleBinModalName = "EmptyRecycleBinModal";

const PackageRecycleBinHubContainer: React.FC<IPackageRecycleBinContainerProps> = ({
  componentState,
  pkgVersions,
  initLoad,
  emptyRecycleBin,
  fakeEmpty,
  hideModal,
  showModal,
}) => {
  const { pageContext } = useComponentContext();

  // Load initial data
  const { feed } = useFeed();
  React.useEffect(() => {
    feed && initLoad(feed);
  }, [initLoad, feed]);

  // UI state
  const [recycleBinListSelection] = useState<IListSelection>(
    () => new ListSelection({ multiSelect: true, selectOnFocus: false }),
  );

  // Protocol hooks
  const commands = useCrossProtocolCommands(
    pkgVersions,
    recycleBinListSelection,
    feed,
    commandTagFilters,
  );
  const protocolReduxModules = useMemo(() => getProtocolReduxModules(pageContext), [pageContext]);

  const showEmptyRecycleBinModal = React.useCallback(() => {
    const onEmptied = () => {
      fakeEmpty();
      hideModal();
    };

    const modalProps = {
      modalId: EmptyRecycleBinModalId,
      props: {
        onDismiss: () => {
          hideModal();
        },
        onEmptyRecycleBin: () => {
          feed && emptyRecycleBin(feed, onEmptied);
        },
      },
    };

    showModal(modalProps, EmptyRecycleBinModalName);
  }, [emptyRecycleBin, fakeEmpty, feed, hideModal, showModal]);

  return (
    <DynamicModuleLoader modules={protocolReduxModules}>
      <PackageRecycleBinHub
        componentState={componentState}
        pkgVersions={pkgVersions}
        recycleBinListSelection={recycleBinListSelection}
        commands={commands}
        emptyRecycleBin={showEmptyRecycleBinModal}
      />
    </DynamicModuleLoader>
  );
};

const mapStateToProps = (
  state: IPackageRecycleBinModuleState,
): IPackageRecycleBinHubContainerMapStateToProps => {
  const hubState = state.packageRecycleBinState;
  return {
    componentState: hubState.componentState,
    pkgVersions: hubState.pkgVersions,
  };
};

const ConnectedPackageRecycleBinHubContainer = connectDynamicModules(
  connect(mapStateToProps, Actions)(PackageRecycleBinHubContainer),
  PackageRecycleBinReduxModule,
);

Components.add(packageRecycleBinHubContainerName, ConnectedPackageRecycleBinHubContainer);

export {
  ConnectedPackageRecycleBinHubContainer as PackageRecycleBinHubContainer,
  IPackageRecycleBinContainerProps,
};
